'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin, AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { ExportedConfig } from '@/app/types/config';
import { isIgnoredTelemetryItem } from '@/app/lib/telemetry/telemetry-filters';

let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;


export default function ApplicationInsightsProvider({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const [isInitialized, setIsInitialized] = useState(false);
    const [, setConfig] = useState<ExportedConfig | null>(null);
    const backendHost = process.env.NEXT_PUBLIC_BACKEND_HOST_EXTERNAL || '';
    const local: boolean = backendHost.indexOf('/localhost') > 0;
    console.log('aps-ui::AppInsigtProvider::local: [' + local + ']');


    useEffect(() => {
        async function fetchConfig() {
            const response = await fetch('/config', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if (!response.ok) {
                const errorData = await response.text();
                console.error('aps-ui::ApplicationInsightsProvider::useEffect: Error:', errorData);
                throw errorData;
            }
            const data: ExportedConfig = await response.json();
            setConfig(data);
            setAppInsightsProvider(data);
        }

        const excludeLocalhostTelemetry = (item: ITelemetryItem): boolean | void => {
            // Discard local traffic and this app's static asset / i18n requests (same rules
            // as the server filter, from telemetry-filters.js)
            if (isIgnoredTelemetryItem(item, window.location.origin)) {
                return false;
            }

            // Return 
            // false if running locally
            // true or void to allow the telemetry item to be processed
            return !local;
        };


        async function setAppInsightsProvider(config: ExportedConfig | null) {
            if (!isInitialized && typeof window !== 'undefined') {
                const browserHistory = createBrowserHistory();
                if (config) {

                    reactPlugin = new ReactPlugin();

                    appInsights = new ApplicationInsights({
                        config: {
                            connectionString: config?.settings.appInsightConnectionString,
                            extensions: [reactPlugin],
                            extensionConfig: {
                                [reactPlugin.identifier]: { history: browserHistory }
                            },
                            enableAutoRouteTracking: true,
                            enableCorsCorrelation: true,
                            enableRequestHeaderTracking: true,
                            enableResponseHeaderTracking: true,
                            disableAjaxTracking: false,
                            disableFetchTracking: false,
                        },
                    });
                    appInsights.loadAppInsights();
                    appInsights.addTelemetryInitializer(excludeLocalhostTelemetry);


                    console.log("aps-ui::ApplicationInsightsProvider::setAppInsightsProvider: Application Insights: Started.");
                    setIsInitialized(true);
                } else {
                    console.error("aps-ui::ApplicationInsightsProvider::setAppInsightsProvider: config not found");
                }
            } else {
                console.log("aps-ui::ApplicationInsightsProvider::setAppInsightsProvider: Application Insights: Already Started.");
            }
        };

        fetchConfig();


    }, [isInitialized, local]);

    useEffect(() => {
        if (appInsights && pathname) {
            appInsights.trackPageView();
        }
    }, [pathname]);

    if (!isInitialized || !reactPlugin) {
        return <></>;
    }

    return (
        <AppInsightsContext.Provider value={reactPlugin}>
            {children}
        </AppInsightsContext.Provider>
    );
}
